import { AfterViewInit, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import {
    ChangeSetItem,
    changeSetItemFactory as cif,
    EntityCacheDispatcher,
    EntityCollectionService,
    EntityServices
} from '@ngrx/data';

import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import {
    filter,
    map,
    startWith,
    take,
    takeWhile,
    tap,
    withLatestFrom
} from 'rxjs/operators';
import { Transition, UiOnExit } from '@uirouter/angular';
import { environment } from '../../../../environments/environment';

import * as fromDataServices from '../../../data-services';
import * as fromShared from '../../../shared';
import { ResponseStatus, SelectItem, uniqBy } from '../../../shared';
import { UnsavedChangesService } from '../../../shared/modal-dialog';
import { LoggingService } from '../../../shared/services/logging';
import { PayrollItem } from '../../item';
import * as fromStore from '../../item/store';
import * as fromEmployeeBaseRateStore from '../store';
import { EmployeePayrollItemService } from '../employee-payroll-item.services';

@Component({
    selector: 'ef-employee-payroll-item-container',
    templateUrl: './employee-payroll-item-container.component.html',
    styleUrls: ['./employee-payroll-item-container.component.scss'],
    standalone: false
})
export class EmployeePayrollItemContainerComponent
    implements OnInit, OnDestroy, AfterViewInit, UiOnExit
{
    payrollHelpHtml = `
<p>EMPLOYEE PAY RATE (left tab):  Add or update the employee’s standard pay rate.</p>
<p>EMPLOYEE PAYROLL ITEM (right tab): Assign payroll items to the employee using the available drop-down options. Newly 
assigned payroll items default to the employee’s standard pay rate.  Pay Rate changes are available on this page only for 
payroll items designated as alternate pay rates (see Payroll Items).</p>`;
    /** True when there are new rows (Status Adding) not yet saved. Used for route and beforeunload guards. */
    hasPendingNewItems = false;
    EmployeePayrollItemModelDS: EntityCollectionService<fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel>;
    EmployeeDS: EntityCollectionService<fromDataServices.ManageUsers.LoginModel>;
    SaveBulkSubject = new ReplaySubject<
        fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    >(1);
    EmployeePayrollItemSubject = new ReplaySubject<
        fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    >(1);
    DeleteItemSubject = new ReplaySubject<
        fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    >(1);
    FillGridSubject = new ReplaySubject<{
        employees: SelectItem[];
        payrollItems: SelectItem[];
        userTypes: SelectItem[];
    }>(1);
    payrollItems$: Observable<PayrollItem[]>;
    payrollItemsSelectItems$: Observable<SelectItem[]>;
    payrollItemTypeSelectItems$: Observable<SelectItem[]>;
    employeeSelectItems$: Observable<SelectItem[]>;
    process$: Observable<fromShared.Process>;
    active = true;
    showSave = false;
    /** Last grid snapshot; used to re-merge unsaved FillGrid rows when combineLatest refreshes from the server. */
    private employeePayrollItemsSnapshot: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[] =
        [];
    public SaveItemSubject =
        new ReplaySubject<fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel>(
            1
        );
    sortedEmployeePayrollItems$ = this.EmployeePayrollItemSubject.asObservable().pipe(
        map(items => this.sortPendingFirstByEmployeeName(items || []))
    );
    baseRateRows$ = this.store.pipe(
        select(fromEmployeeBaseRateStore.selectAll)
    );
    constructor(
        private log: LoggingService,
        private store: Store<fromStore.State>,
        private confirmService: UnsavedChangesService,
        public dispatcher: EntityCacheDispatcher,
        public entityServices: EntityServices,
        private service: EmployeePayrollItemService
    ) {
        this.log.verbose({ type: 'EmployeePayrollItemContainerComponent::' });
        this.EmployeePayrollItemModelDS =
            entityServices.getEntityCollectionService<fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel>(
                fromDataServices.EmployeePayrollItem.entityName
            );
        this.EmployeeDS =
            entityServices.getEntityCollectionService<fromDataServices.ManageUsers.LoginModel>(
                fromDataServices.ManageUsers.entityName
            );
        this.store.dispatch(fromStore.getPayrollItems());
    }
    ngOnDestroy(): void {
        this.active = false;
    }

    uiCanExit(transition: Transition): boolean | Promise<boolean> {
        if (!this.hasPendingNewItems) {
            return true;
        }
        return this.confirmService
            .confirmPromise({
                dialogText:
                    'You have new employee payroll items that have not been saved. Leave this page and discard them?',
                okText: 'Leave',
                cancelText: 'Stay'
            })
            .then(() => true)
            .catch(() => {
                return Promise.reject(new Error('Navigation aborted by user'));
            });
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: Event): void {
        if (this.hasPendingNewItems) {
            event.preventDefault();
            (event as unknown as { returnValue: string }).returnValue = '';
        }
    }
    ngOnInit(): void {
        this.EmployeeDS.clearCache();
        this.EmployeeDS.getWithQuery({
            ShowInactive: 'true',
            UserFilterID: '7'
        });

        this.EmployeePayrollItemModelDS.clearCache();
        this.EmployeePayrollItemModelDS.getAll();

        this.store.dispatch(fromEmployeeBaseRateStore.getEmployeeBaseRates(true));
    }
    onSaveBaseRate(row: fromEmployeeBaseRateStore.EmployeeBaseRateStateItem): void {
        this.store.dispatch(
            fromEmployeeBaseRateStore.putEmployeeBaseRate({
                request: {
                    EmployeeBaseRateList: [
                        {
                            EmployeeListID: row.EmployeeListID,
                            EmployeeName: row.EmployeeName,
                            EmpRateType: row.EmpRateType,
                            BaseRate: row.BaseRate
                        }
                    ]
                }
            })
        );
    }
    ngAfterViewInit(): void {
        setTimeout(() => {
            this.setupObservables();
            this.setupSubscriptions();
        }, 0);
    }
    replaceElement(
        index: number,
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[],
        newItem: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel | null,
        pis: PayrollItem[]
    ) {
        if (newItem != null) {
            const pi = pis.find(p => p.PIListID === newItem.PIListID);
            newItem = {
                ...newItem,
                id: `${newItem.EmployeeListID}-${newItem.PIListID}`,
                IsAlternateHourly: pi?.IsAlternateHourly === true,
                Status: {
                    StatusCode: ResponseStatus.Success,
                    Message: 'Saved'
                }
            };
            items.splice(index, 1, newItem);
        } else {
            items.splice(index, 1);
        }

        
        return items;
    }

    /** Default table ordering: pending rows first, both groups by employee name ascending. */
    private sortPendingFirstByEmployeeName(
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ): fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[] {
        const pending = items.filter(
            i => i?.Status?.StatusCode === ResponseStatus.Adding
        );
        const rest = items.filter(
            i => i?.Status?.StatusCode !== ResponseStatus.Adding
        );
        const byName = (
            a: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel,
            b: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel
        ) =>
            (a?.Employeename || '')
                .toLocaleLowerCase()
                .localeCompare((b?.Employeename || '').toLocaleLowerCase())
        ;
        return [...pending.sort(byName), ...rest.sort(byName)];
    }

    private emitEmployeePayrollItems(
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ): void {
        this.employeePayrollItemsSnapshot = items;
        this.EmployeePayrollItemSubject.next(items);
    }

    /**
     * When employees or payroll items update, combineLatest rebuilds from the entity cache only.
     * Preserve in-memory unsaved rows (FillGrid / Status Adding) unless the server now has the same assignment.
     */
    /** Employee + payroll item assignment; stable across UI copies of the same row. */
    private payrollItemAssignmentKey(
        row: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel
    ): string {
        return `${row.EmployeeListID}-${row.PIListID}`;
    }

    private mergePendingAddsWithEnrichedServer(
        enriched: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ): fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[] {
        const pending = this.employeePayrollItemsSnapshot.filter(
            row =>
                row?.Status?.StatusCode === ResponseStatus.Adding ||
                String(row?.id ?? '').startsWith('add')
        );
        const serverKeys = new Set(enriched.map(r => this.payrollItemAssignmentKey(r)));
        const pendingToKeep = pending.filter(
            p => !serverKeys.has(this.payrollItemAssignmentKey(p))
        );
        return [...pendingToKeep, ...enriched];
    }

    private isAllowedPayrollItemForDropdown(item: PayrollItem): boolean {
        return !((item?.PayrollItem ?? '').trim().toUpperCase().startsWith('OT_'));
    }

    private normalizeEmpRateType(value: string | undefined): string {
        const normalized = (value ?? '').trim().toLocaleLowerCase();
        if (normalized.startsWith('salary')) {
            return 'salary';
        }
        if (normalized.startsWith('hourly')) {
            return 'hourly';
        }
        return normalized;
    }

    private getBaseRateForEmployeePayrollItem(
        employeeListId: string,
        payrollItemType: string | undefined,
        baseRateRows: fromEmployeeBaseRateStore.EmployeeBaseRateStateItem[]
    ): number | undefined {
        const targetRateType = this.normalizeEmpRateType(payrollItemType);
        return baseRateRows.find(
            row =>
                row.EmployeeListID === employeeListId &&
                this.normalizeEmpRateType(row.EmpRateType) === targetRateType
        )?.BaseRate;
    }

    setupObservables() {
        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        this.payrollItems$ = this.store
            .pipe(select(fromStore.selectAll))
            .pipe(
                map(items =>
                    clone(items).sort((a: PayrollItem, b: PayrollItem) =>
                        a.PayrollItem.toLocaleLowerCase().localeCompare(
                            b.PayrollItem.toLocaleLowerCase()
                        )
                    )
                )
            );

        const activePayrollItems$ = this.store.pipe(
            select(fromStore.selectActivePayrollItems)
        ).pipe(
            map(items =>
                clone(items).sort((a: PayrollItem, b: PayrollItem) =>
                    a.PayrollItem.toLocaleLowerCase().localeCompare(
                        b.PayrollItem.toLocaleLowerCase()
                    )
                )
            )
        );

        this.payrollItemTypeSelectItems$ = this.payrollItems$.pipe(
            map(items =>
                uniqBy(
                    items
                        .map(item => ({
                            label: item.PayrollItemType,
                            value: item.PayrollItemType
                        }))
                        .concat([
                            { label: 'Salary', value: 'Salary' },
                            { label: 'Hourly', value: 'Hourly' }
                        ]),
                    x => x.value
                )
            )
        );
        this.payrollItemsSelectItems$ = activePayrollItems$.pipe(
            map(items =>
                items
                    .filter(item => this.isAllowedPayrollItemForDropdown(item))
                    .map(item => ({
                    label: item.PayrollItem,
                    value: item.PIListID,
                    payrollItemType: item.PayrollItemType
                    }))
            )
        );
        // this.EmployeePayrollItemSubject.next(this.initialData);
        this.employeeSelectItems$ = this.EmployeeDS.entities$.pipe(
            map(items =>
                items
                    .filter(item => item.IsActive)
                    .filter(item => !item.IsConsultant)
                    .map(item => ({
                        label: item.EmployeeName,
                        value: item.EmployeeListID
                    }))
            )
        );
    }
    setupSubscriptions() {
        this.EmployeePayrollItemSubject.pipe(
            takeWhile(() => this.active)
        ).subscribe(items => {
            this.hasPendingNewItems =
                items?.some(
                    p => p?.Status?.StatusCode === ResponseStatus.Adding
                ) ?? false;
        });
        combineLatest([
            this.EmployeePayrollItemModelDS.entities$.pipe(
                takeWhile(() => this.active),
                map(items => items.filter(item => item.IsActive)),
                map(items => [...clone(items)])
            ),
            this.EmployeeDS.entities$,
            this.payrollItems$
        ]).subscribe(([items, employees, payrollItems]) => {
            this.log.verbose({
                type: 'EmployeePayrollItemContainerComponent:EmployeePayrollItemModelDS.entities$',
                items,
                employees,
                payrollItems
            });
            const enriched = items.map(item => {
                const pi = payrollItems.find(
                    p => p.PIListID === item.PIListID
                );
                return {
                    ...item,
                    Employeename:
                        employees.find(
                            e => e.EmployeeListID === item.EmployeeListID
                        )?.EmployeeName || item.EmployeeListID,
                    Usertype:
                        employees.find(
                            e => e.EmployeeListID === item.EmployeeListID
                        )?.Usertype ?? item.Usertype ?? '',
                    Payrollitem: pi?.PayrollItem || item.PIListID,
                    IsAlternateHourly: pi?.IsAlternateHourly === true
                };
            });
            this.emitEmployeePayrollItems(
                this.mergePendingAddsWithEnrichedServer(enriched)
            );
        });
        this.DeleteItemSubject.pipe(
            withLatestFrom(this.EmployeePayrollItemSubject),
            takeWhile(() => this.active)
        ).subscribe(([deleted, items]) => {
            this.confirmService
                    .confirmPromise({
                        okText: 'Yes',
                        cancelText: 'No',
                        dialogText: `Are you sure you want to delete: </br>
                        ${deleted[0].Payrollitem} with a rate of ${deleted[0].PayRate} for ${deleted[0].Employeename}?`
                    }).then(() => {
                        this.log.verbose({
                            type: 'EmployeePayrollItemContainerComponent:deleting',
                            deleted
                        });
                        const newItems = items.filter(
                            item => !deleted.some(x => x.id === item.id)
                        );
                        // Update grid before saveBulk: SaveBulkSubject runs synchronously and
                        // withLatestFrom must see the list without deleted rows.
                        this.emitEmployeePayrollItems(newItems);
                        // Delete should bypass onSave pay-rate validation:
                        // an inactive row must persist even when PayRate is 0.
                        this.saveBulk(
                            deleted
                                .filter(item => !item.id.toString().startsWith('add'))
                                .map(item => ({ ...item, IsActive: false }))
                        );
                    }).catch(() => {});
        });
        this.SaveBulkSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(
                this.EmployeePayrollItemSubject,
                this.payrollItems$
            )
        ).subscribe(([saved, currentItems, pis]) => {
            this.service
                .save(saved)
                .pipe(take(1))
                .subscribe(resp => {
                    this.log.verbose({
                        type: 'EmployeePayrollItemContainerComponent:saveBulk:response',
                        resp,
                        saved,
                        currentItems
                    });

                    const mappedResp = resp.map(item => {
                        const pi = pis.find(
                            p => p.PIListID === item.PIListID
                        );
                        return {
                            ...item,
                            id: `${item.EmployeeListID}-${item.PIListID}`,
                            IsAlternateHourly:
                                pi?.IsAlternateHourly === true,
                            Status: {
                                StatusCode: ResponseStatus.Success,
                                Message: 'Saved'
                            }
                        };
                    });
                    // Preserve row order: replace matching rows in place.
                    let newItems = currentItems.slice();
                    mappedResp.forEach(item => {
                        const key = this.payrollItemAssignmentKey(item);
                        const index = newItems.findIndex(
                            x => this.payrollItemAssignmentKey(x) === key
                        );
                        if (index === -1) {
                            if (item.IsActive) {
                                newItems.push(item);
                            }
                            return;
                        }
                        if (item.IsActive) {
                            newItems.splice(index, 1, item);
                        } else {
                            newItems.splice(index, 1);
                        }
                    });
                    if (this.showSave == true) {
                        this.EmployeePayrollItemModelDS.updateManyInCache(resp);
                    } else {
                        this.EmployeePayrollItemModelDS.upsertManyInCache(resp);
                    }
                    this.log.verbose({
                        type: 'EmployeePayrollItemContainerComponent:saveBulk:response',
                        newItems
                    });
                    this.emitEmployeePayrollItems(newItems);
                });
        });
        this.SaveItemSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.EmployeePayrollItemSubject),
            withLatestFrom(this.payrollItems$),
            filter(
                ([[item, items], pis]) =>
                    (pis
                        .find(p => p.PIListID === item.PIListID)
                        ?.PayrollItemType.toLocaleLowerCase()
                        .startsWith('salary') ||
                        false) &&
                    !item.id.toString().startsWith('add')
            )
        ).subscribe(([[item, items], pis]) => {
            items = items.map(x => ({
                ...x,
                PayRate:
                    x.EmployeeListID === item.EmployeeListID &&
                    pis
                        .find(p => p.PIListID === x.PIListID)
                        ?.PayrollItemType.toLocaleLowerCase()
                        .startsWith('salary')
                        ? item.PayRate
                        : x.PayRate
            }));
            this.log.verbose({
                type: 'EmployeePayrollItemContainerComponent:SaveItemSubject:salary',
                item,
                items,
                pis
            });
            this.emitEmployeePayrollItems(items);
            this.saveBulk(
                items
                    .filter(x => x.EmployeeListID === item.EmployeeListID)
                    .filter(
                        x =>
                            pis
                                .find(p => p.PIListID === x.PIListID)
                                ?.PayrollItemType.toLocaleLowerCase()
                                .startsWith('salary') || false
                    )
            );
        });
        this.SaveItemSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(this.EmployeePayrollItemSubject),
            withLatestFrom(this.payrollItems$),
            filter(
                ([[item, items], pis]) =>
                    pis
                        .find(p => p.PIListID === item.PIListID)
                        ?.PayrollItemType.toLocaleLowerCase()
                        .startsWith('hourly') || false
            )
        ).subscribe(([[item, items], pis]) => {
            this.log.verbose({
                type: 'EmployeePayrollItemContainerComponent:SaveItemSubject:hourly',
                item,
                items,
                pis
            });
            const updated = items.map(x =>
                x.EmployeeListID === item.EmployeeListID &&
                x.PIListID === item.PIListID
                    ? { ...x, PayRate: item.PayRate }
                    : x
            );
            this.emitEmployeePayrollItems(updated);
            if (this.showSave) {
                return;
            }
            this.saveBulk([item]);
        });
        this.FillGridSubject.pipe(
            takeWhile(() => this.active),
            withLatestFrom(
                this.EmployeePayrollItemSubject.pipe(startWith([])),
                this.payrollItems$,
                this.EmployeeDS.entities$,
                this.baseRateRows$
            )
        ).subscribe(([item, currentItems, pis, employees, baseRates]) => {
            const items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[] =
                currentItems.slice();
            let id = 1;

            const salaryPIs = pis
                .filter(p =>
                    p.PayrollItemType?.toLocaleLowerCase()?.startsWith('salary')
                )
                .map(p => p.PIListID);

            item.employees.forEach(employee => {
                const salaryRate = currentItems.find(
                    i =>
                        salaryPIs.some(p => p === i.PIListID) &&
                        i.EmployeeListID === employee.value
                )?.PayRate;
                item.payrollItems.forEach(payrollItem => {
                    if (
                        !items.some(
                            x =>
                                x.EmployeeListID === employee.value &&
                                x.PIListID === payrollItem.value
                        )
                    ) {
                        const payrollItemType = pis.find(
                            p => p.PIListID === payrollItem.value
                        )?.PayrollItemType;
                        items.push({
                            id: `add-${id++}`,
                            EmployeeListID: employee.value,
                            Employeename: employee.label as string,
                            Usertype:
                                employees.find(
                                    e => e.EmployeeListID === employee.value
                                )?.Usertype ?? '',
                            PIListID: payrollItem.value,
                            Payrollitem: payrollItem.label as string,
                            IsAlternateHourly:
                                pis.find(p => p.PIListID === payrollItem.value)
                                    ?.IsAlternateHourly === true,
                            IsActive: true,
                            PayRate:
                                this.getBaseRateForEmployeePayrollItem(
                                    employee.value as string,
                                    payrollItemType,
                                    baseRates
                                ) ??
                                (salaryPIs.some(
                                    p => p === payrollItem.value
                                )
                                    ? salaryRate ?? 0
                                    : 0),
                            Status: {
                                Message: 'Adding...',
                                StatusCode: ResponseStatus.Adding
                            }
                        });
                    }
                });
            });
            this.emitEmployeePayrollItems(items);
        });
    }
    public onFilterPayrollItemType(values: string[]): void {
        this.log.verbose({
            type: 'EmployeePayrollItemContainerComponent: onFilterPayrollItemType',
            values
        });
        this.payrollItemsSelectItems$ = this.payrollItems$.pipe(
            map(items =>
                items
                    .filter(item => values.includes(item.PayrollItemType))
                    .filter(item => this.isAllowedPayrollItemForDropdown(item))
            ),
            map(items =>
                clone(items).sort((a: PayrollItem, b: PayrollItem) =>
                    a.PayrollItem.toLocaleLowerCase().localeCompare(
                        b.PayrollItem.toLocaleLowerCase()
                    )
                )
            ),
            map(items =>
                items
                    .filter(item => values.includes(item.PayrollItemType))
                    .map(item => ({
                        label: item.PayrollItem,
                        value: item.PIListID,
                        payrollItemType: item.PayrollItemType
                    }))
            )
        );
    }
    public onEditItem(
        item: Partial<fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel>
    ): void {
        this.log.verbose({
            type: 'EmployeePayrollItemContainerComponent: onEditItem',
            item
        });
    }
    public onSave(
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ): void {
        this.log.verbose({
            type: 'EmployeePayrollItemContainerComponent: onSave',
            items
        });
        if (items.some(item => (item?.PayRate || 0) <= 0)) {
            this.log.verbose({
                type: 'EmployeePayrollItemContainerComponent: no payrate',
                nopayitems: items.filter(item => (item?.PayRate || 0) <= 0),
                items: items
            });
            this.confirmService
                .confirmPromise({
                    dialogText:
                        'One or more items have a pay rate of 0. These items will not be saved, Continue?'
                })
                .then(() => {
                    this.saveBulk(items.filter(x => x.PayRate > 0));
                })
                .catch(() => {});
        } else {
            this.saveBulk(items);
        }
    }
    onDeleteItem(
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ) {
        //TODO: move to observable and combine with EmployeePayrollItemSubject
        this.log.verbose({
            type: 'EmployeePayrollItemContainerComponent:onDeleteItem',
            items
        });
        this.DeleteItemSubject.next(items);
    }
    public saveBulk(
        items: fromDataServices.EmployeePayrollItem.EmployeePayrollItemModel[]
    ) {
        this.log.verbose({
            type: 'EmployeePayrollItemContainerComponent: saveBulk',
            items
        });
        if (items && items.length > 0) {
            this.SaveBulkSubject.next(items);
        }
    }
    onShowSave($event) {
        this.showSave = $event;
    }
}
